import React, { useEffect, useRef, useState } from 'react';
import { authenticatedFetch } from '../api/client';
import './DashboardAssistant.css';
import WidgetBuilder, { builderRequest } from './WidgetBuilder';

export function canUseDashboardAssistant(user) {
  return Boolean(user && user.is_active !== false &&
    (user.role === 'admin' || user.email?.trim().toLowerCase() === 'mohamed.benzarti@mbzuai.ac.ae'));
}

async function assistantRequest(path, options = {}) {
  const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL || ''}/api/assistant${path}`, {
    ...options, headers: { 'Content-Type': 'application/json' },
  });
  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.error || 'The assistant is unavailable.');
    error.code = data.code;
    throw error;
  }
  return data;
}

export default function DashboardAssistant({ widgets, onApply, widgetData, onPublishWidget, onRemoveWidget }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [agreement, setAgreement] = useState(null);
  const [agreeChecked, setAgreeChecked] = useState(false);
  const [agreementBusy, setAgreementBusy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [showWidgets, setShowWidgets] = useState(false);
  const [conversation, setConversation] = useState(0);
  const [buildStatus, setBuildStatus] = useState('Thinking…');
  const agreementDialog = useRef(null);
  const termsVisible = Boolean(open && agreement && (!agreement.accepted || showTerms));
  useEffect(() => {
    if (termsVisible) {
      setAgreeChecked(false);
      agreementDialog.current?.showModal?.();
    }
  }, [termsVisible]);
  const pending = useRef(null);
  const bottom = useRef(null);
  useEffect(() => () => pending.current?.abort(), []);
  useEffect(() => { bottom.current?.scrollIntoView?.({ block: 'nearest' }); }, [messages, busy, open]);
  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    setAgreementBusy(true);
    assistantRequest('/agreement', { signal: controller.signal }).then(result => {
      if (!controller.signal.aborted) setAgreement(result);
    }).catch(err => { if (!controller.signal.aborted) setError(err.message); })
      .finally(() => { if (!controller.signal.aborted) setAgreementBusy(false); });
    return () => controller.abort();
  }, [open]);

  function newConversation() {
    pending.current?.abort();
    pending.current = null;
    setMessages([]); setDraft(''); setError(''); setBusy(false);
    setSelectedWidget(null); setShowWidgets(false); setConversation(value => value + 1);
  }

  async function acceptAgreement() {
    if (!agreeChecked || agreementBusy) return;
    setAgreementBusy(true); setError('');
    try {
      setAgreement(await assistantRequest('/agreement', { method: 'POST',
        body: JSON.stringify({ accepted: true, version: agreement.version }) }));
      setShowTerms(false);
    } catch (err) { setError(err.message); }
    finally { setAgreementBusy(false); }
  }

  async function send(event) {
    event.preventDefault();
    if (!agreement?.accepted || !draft.trim() || busy) return;
    const next = [...messages, { role: 'user', content: draft.trim() }];
    setMessages(next); setDraft(''); setBusy(true); setError(''); setBuildStatus('Thinking…');
    const controller = new AbortController();
    pending.current = controller;
    const timer = setTimeout(() => controller.abort(), 240000);
    try {
      const data = await assistantRequest('/chat', {
        method: 'POST', signal: controller.signal,
        body: JSON.stringify({ messages: next.slice(-20).map(({ role, content, status }) => ({
          role, content: status ? `${content}\nAction status: ${status}` : content,
        })), widgets, selectedWidgetId: selectedWidget?.id }),
      });
      if (pending.current !== controller) return;
      let widget;
      if (data.build) {
        setBuildStatus('Creating your widget draft…');
        const result = await builderRequest('/draft', { method: 'POST', signal: controller.signal,
          body: JSON.stringify(data.build) });
        if (pending.current !== controller) return;
        widget = result.widget;
        setSelectedWidget(widget); setShowWidgets(true);
      }
      setMessages(current => [...current.map(m => m.actions?.length && !m.status ? { ...m, status: 'superseded' } : m),
        { role: 'assistant', content: widget ? `${data.message}\nDraft ready: ${widget.name}. Preview it below before adding it to your canvas.` : data.message, actions: data.actions }]);
    } catch (err) {
      if (pending.current !== controller) return;
      if (err.code === 'agreement_required') setAgreement(current => current && ({ ...current, accepted: false }));
      setError(err.name === 'AbortError' ? 'Request timed out. Check Saved widgets for a completed draft before retrying.' : err.message);
      setDraft(next[next.length - 1].content);
      setMessages(current => current.slice(0, -1));
    } finally {
      clearTimeout(timer);
      if (pending.current === controller) { pending.current = null; setBusy(false); }
    }
  }

  function apply(index) {
    try {
      onApply(messages[index].actions);
      setMessages(current => current.map((m, i) => i === index ? { ...m, status: 'applied' } : m));
      setError('');
    } catch (err) { setError(err.message); }
  }

  return <div className="dashboard-assistant" onMouseDown={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
    <button type="button" className="assistant-toggle" onClick={() => setOpen(!open)} aria-expanded={open} aria-controls="dashboard-assistant-panel">✦ Assistant</button>
    {open && <section id="dashboard-assistant-panel" className="assistant-panel" aria-label="Dashboard assistant">
      <header><strong>Assistant</strong><div className="assistant-actions">
        {agreement?.accepted && <button type="button" onClick={newConversation} title="Clear chat; keep saved widgets and canvas changes">New conversation</button>}
        <button type="button" aria-label="Close assistant" onClick={() => setOpen(false)}>×</button>
      </div></header>
      {agreementBusy && !agreement && <p role="status" className="assistant-note">Loading agreement…</p>}
      {!agreement && !agreementBusy && <button type="button" onClick={() => { setOpen(false); setError(''); }}>Close and try again</button>}
      {termsVisible && <dialog ref={agreementDialog} className="assistant-agreement" aria-labelledby="assistant-agreement-title" onCancel={event => { event.preventDefault(); if (!agreementBusy) { setShowTerms(false); if (!agreement.accepted) setOpen(false); } }}>
        <h3 id="assistant-agreement-title">Assistant End User License Agreement</h3>
        <p>Please read and accept before using the assistant.</p>
        {agreement.sections.map(section => <section key={section.title}><h4>{section.title}</h4><p>{section.text}</p></section>)}
        <small>Version {agreement.version}</small>
        {agreement.accepted ? <button type="button" onClick={() => setShowTerms(false)}>Back to conversation</button> : <>
          <label className="assistant-agreement-check"><input type="checkbox" checked={agreeChecked} onChange={e => setAgreeChecked(e.target.checked)} />I have read and agree to the Assistant End User License Agreement.</label>
          <div className="assistant-actions"><button type="button" disabled={!agreeChecked || agreementBusy} onClick={acceptAgreement}>Accept and continue</button>
            <button type="button" disabled={agreementBusy} onClick={() => { setAgreeChecked(false); setOpen(false); }}>Not now</button></div>
        </>}
        {error && <p className="assistant-error" role="alert">{error}</p>}
      </dialog>}
      {agreement?.accepted && !showTerms && <>
        <p className="assistant-note">Ask a question, change your layout, or describe a new widget—all in this chat.</p>
        <div className="assistant-tools"><button type="button" disabled={busy} onClick={() => setShowWidgets(!showWidgets)} aria-expanded={showWidgets}>Saved widgets</button>
          <button type="button" onClick={() => setShowTerms(true)}>Usage agreement</button></div>
        <div className="assistant-messages" role="log" aria-live="polite">
          {!messages.length && <p>How can I help? Try “Show waveforms” or “Create a chart comparing spike counts.”</p>}
          {messages.map((m, i) => <article key={i} className={`assistant-message ${m.role}`}>
            <strong>{m.role === 'user' ? 'You' : 'Assistant'}</strong><p>{m.content}</p>
            {!!m.actions?.length && <><ul>{m.actions.map((a, j) => <li key={j}>{a.operation}: {widgets.find(w => w.id === a.widgetId)?.name || a.widgetId}</li>)}</ul>
              {m.status ? <small>{m.status === 'applied' ? 'Changes applied' : m.status === 'dismissed' ? 'Dismissed' : 'Replaced by a newer reply'}</small> : <div className="assistant-actions">
                <button type="button" disabled={busy} onClick={() => apply(i)}>Apply changes</button>
                <button type="button" disabled={busy} onClick={() => setMessages(current => current.map((item, j) => i === j ? { ...item, status: 'dismissed' } : item))}>Dismiss</button>
              </div>}
            </>}
          </article>)}
          {busy && <p role="status">{buildStatus}</p>}
          {showWidgets && !busy && <WidgetBuilder key={`${conversation}:${selectedWidget?.id || 'saved'}`} conversationMode
            initialWidget={selectedWidget} onSelect={setSelectedWidget} data={widgetData} onPublish={onPublishWidget} onRemove={onRemoveWidget} />}
          <div ref={bottom} />
        </div>
        <form onSubmit={send}><label htmlFor="assistant-message">{selectedWidget ? `Message · selected widget: ${selectedWidget.name}` : 'Message'}</label><textarea id="assistant-message" value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={event => {
          if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
            event.preventDefault(); send(event);
          }
        }} maxLength={4000} disabled={busy || agreementBusy} placeholder="What would you like to do?" rows={2} /><button type="submit" disabled={busy || agreementBusy || !draft.trim()}>Send</button></form>
      </>}
      {error && !termsVisible && <p className="assistant-error" role="alert">{error}</p>}
    </section>}
  </div>;
}
